/**
 * The product mark.
 *
 * Traced from the FINAL APPROVED logo — the pack the founder supplied on 2026-09-12
 * (`Developments/Sign/Logos all v2`: a transparent master, eight size categories, and a
 * `Web_Icons` folder), redrawn as geometry in the master's own pixel space so every number can be
 * checked against the 512 px icon with a ruler. Geometry rather than the pack's PNGs because a
 * vector is sharp at 20 px in a sidebar and at whatever the sign-in panel is set to, costs one
 * inline SVG instead of a request on first paint, and can take a plate from the theme (below).
 *
 * **The numbers are in `brand-geometry.ts`, not here.** This file maps that list to JSX; the same
 * list is rendered to an SVG string by `markSvg()` for the one consumer that cannot run React —
 * the script that bakes the mail's PNG (`infra/render-product-mark.mjs`) — so the mark in an
 * email and the mark in the sidebar cannot drift apart.
 *
 * ## What the mark is
 *
 * Six elements, all of them in the artwork and none invented:
 *
 *   1. a bold **F** whose top-left corner flows round into the arm, on a blue ramp — bright royal
 *      blue at the arm, deep indigo at the foot of the stem;
 *   2. a light-blue **swoosh** that crosses the stem rising to the right and leaves it as a
 *      **petal** where the crossbar would be — the "flow" in the name;
 *   3. a fainter second wave lower on the stem;
 *   4. a green **pen** at 36° from vertical, its cap end rounded, a pale seam where the cap meets
 *      the barrel, and a darker nib ending in a near-black tip;
 *   5. a **signature** under the nib: a hook, then a flourish running right;
 *   6. and NO TILE. The approved mark sits bare on its ground. What it needs on a dark or a
 *      saturated ground is a light plate — the same answer the email gives a tenant's logo
 *      (brand-fragments.ts, "The white plate"). Two tokens carry it (tokens.css):
 *      `--brand-plate` decides whether this ground needs one, and `--brand-plate-solid` is what
 *      it is painted. The second follows the theme — white on a light page's accent panel, the
 *      paper tone in the dark theme so a small bright card is not a glare block — and the theme
 *      may move only that tint, because the colours above are fixed literals and every one of
 *      them dies on a dark ground. tokens.css carries the measurement.
 *
 * The wordmark beside it is the pack's too: **FlowZa** in the logo's navy, **Sign** in green
 * script (`Wordmark` below). The pack's lockups are all STACKED — mark above name — and that is
 * what the sign-in panel draws; the sidebar sets the two side by side because 56 px of header
 * cannot hold a stack, and dropping the mark beside the name is the ordinary adaptation every
 * responsive logo makes.
 *
 * ## Two forms, and why a logo is allowed to have them
 *
 * At 40 px and up all six elements read. At 24 px — where this actually lives, in the sidebar —
 * the seam is a hairline, the signature is a dash and the tip is a dot; six elements inside a
 * 24 px mark produce mush, and the first thing lost is the letter. So `Logo` draws the full mark
 * at 40 px and up and a SIMPLIFIED one below it: the same F, the same swoosh and petal, and the
 * pen as barrel plus nib without its seam and tip. Nothing moves and nothing is invented for the
 * small form; detail is dropped, which is what every responsive logo does.
 *
 * ## Why the mark does NOT follow the tenant's accent, when everything around it does
 *
 * ADR 0054: a tenant does not white-label OUR name, they replace it. `applyBranding()` repaints
 * the console AROUND the mark — surfaces, accent, buttons, the whole semantic layer — and the mark
 * carries its own colours. Those colours are literals here, and this is the one file in the
 * design system allowed to write a hex: a logo is not a themeable surface, it has to be the same
 * object on a white page and on a white plate. The two WORDS are different — they sit on the page
 * rather than on the plate — so they take `--brand-ink` and `--brand-signature`, which follow the
 * theme and which no tenant may set (brand-tokens.ts).
 *
 * The wordmark stays live type rather than an image: it is selectable, it scales with the reader's
 * font size, a screen reader reads it, and it costs nothing to download. The script face for
 * "Sign" is Dancing Script 700 (`--font-script`), self-hosted like every other face the product
 * ships and the nearest of them to the pack's brush script; if it fails to load the word falls
 * back to the sans stack rather than to whatever the operating system calls `cursive`.
 */

import {
  BOX,
  FULL_DETAIL_FROM,
  LOGO_ASPECT,
  MARK,
  MARK_ELEMENTS,
  MARK_GRADIENTS,
  drawn,
  type MarkElement,
  type MarkFill,
} from './brand-geometry'

export { LOGO_ASPECT } from './brand-geometry'

/**
 * The heights the mark is drawn at. `FULL_DETAIL_FROM` and up draw the full mark; below it, the
 * simplified one. A closed set rather than `number`, so a caller cannot ask for a size nobody has
 * looked at — the two forms are chosen by this number and the drawing is checked at each of them.
 */
export type LogoSize = 20 | 24 | 26 | 28 | 30 | 32 | 40 | 42 | 48 | 64 | 104

export type LogoProps = {
  /** Height in CSS pixels; the width follows by `LOGO_ASPECT`. */
  readonly size?: LogoSize
  readonly className?: string
  /**
   * Force the plate ON, for the accent-painted sign-in panel — a ground the theme cannot know
   * about, because it is painted by a class and not by the palette. It forces the plate's
   * PRESENCE, not its colour: the colour is `--brand-plate-solid` either way, so an accent panel
   * in the dark theme draws the same paper tone as the dark page beside it. Everywhere else the
   * decision is `--brand-plate`, which is `transparent` on a light page and the plate colour on a
   * dark one.
   */
  readonly inverted?: boolean
}

/**
 * `id`s inside an inline SVG are document-global, so two marks on one page — the sidebar and a
 * dialog, the auth panel and its small-screen twin — would both point at whichever gradient was
 * defined last. A counter keyed per module gives each instance its own, which is what React's
 * `useId` is for; this is a leaf with no other reason to be a client component, so it does it by
 * hand rather than by pulling in a hook.
 */
let markSeq = 0

export function Logo({ size = 28, className = '', inverted = false }: LogoProps) {
  markSeq += 1
  const ids = `fz-${markSeq}`
  const detailed = size >= FULL_DETAIL_FROM
  const fillOf = (fill: MarkFill): string =>
    typeof fill === 'string' ? fill : `url(#${ids}-${fill.gradient})`

  // The JSX twin of `markSvg()` in brand-geometry.ts: one list, two renderers, and a change to
  // the drawing is a change to both. Whether an element is drawn at this size is `drawn()`'s
  // decision, made in one place for both forms.
  const draw = (el: MarkElement, key: number): JSX.Element | null => {
    if (!drawn(el, detailed)) return null
    switch (el.kind) {
      case 'fill':
        return (
          <path
            key={key}
            d={el.d}
            fill={fillOf(el.fill)}
            {...(el.opacity === undefined ? {} : { fillOpacity: el.opacity })}
          />
        )
      case 'rect':
        return (
          <rect
            key={key}
            x={el.x}
            y={el.y}
            width={el.width}
            height={el.height}
            fill={el.fill}
            {...(el.opacity === undefined ? {} : { fillOpacity: el.opacity })}
          />
        )
      case 'stroke':
        return (
          <path
            key={key}
            d={el.d}
            fill="none"
            stroke={el.stroke}
            strokeWidth={el.width}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )
      case 'group':
        return (
          <g key={key} transform={el.transform}>
            {el.children.map(draw)}
          </g>
        )
    }
  }

  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width={Math.round(size * LOGO_ASPECT)}
      height={size}
      viewBox={`${BOX.x} ${BOX.y} ${BOX.width} ${BOX.height}`}
      className={`shrink-0 ${className}`}
    >
      <defs>
        {MARK_GRADIENTS.map((g) => (
          <linearGradient
            key={g.id}
            id={`${ids}-${g.id}`}
            gradientUnits="userSpaceOnUse"
            x1={g.x1}
            y1={g.y1}
            x2={g.x2}
            y2={g.y2}
          >
            {g.stops.map((stop) => (
              <stop key={stop.offset} offset={stop.offset} stopColor={stop.color} />
            ))}
          </linearGradient>
        ))}
      </defs>

      {/* The plate: nothing on a light page, the theme's plate colour on a dark one, and forced on
          for the accent panel. The literal is the FALLBACK rather than the value, so a document
          that never loaded tokens.css — a unit test, a fragment rendered on its own — still gets a
          plate under a mark that needs one, and every themed document gets the theme's. */}
      <rect
        x={BOX.x}
        y={BOX.y}
        width={BOX.width}
        height={BOX.height}
        rx={BOX.radius}
        style={{
          fill: inverted ? `var(--brand-plate-solid, ${MARK.plate})` : 'var(--brand-plate)',
        }}
      />

      {MARK_ELEMENTS.map(draw)}
    </svg>
  )
}

export type WordmarkProps = {
  readonly size?: 'sm' | 'md' | 'lg'
  readonly className?: string
  /** Colour of the two words; the default follows the theme's brand tokens. */
  readonly tone?: 'inherit' | 'on-accent'
  /**
   * `inline` sets the mark beside the name, for a header. `stacked` sets it above, which is the
   * lockup the pack supplies and the one the sign-in panel draws.
   */
  readonly layout?: 'inline' | 'stacked'
}

/**
 * The mark's height and the name's size, per step.
 *
 * `lg`'s stacked mark is 42 px, which is 40% of the 104 px it was — the founder's number, set on
 * 2026-09-15 after seeing the sign-in panel live: a 104 px mark is as wide as the wordmark under
 * it and the pair reads as a brand plate rather than as a sign-in page. 42 rather than 41.6,
 * because `Logo` takes a closed set of sizes and because `FULL_DETAIL_FROM` is 40 — at 42 the
 * second wave, the pen's seam and tip and the signature under the nib are all still drawn, and a
 * rounding DOWN to 40 would sit on that edge. The mark is now the smaller half of the lockup and
 * the name is the subject, which is what a sign-in page wants.
 */
const WORDMARK_SIZE: Record<
  NonNullable<WordmarkProps['size']>,
  { inline: LogoSize; stacked: LogoSize; text: string }
> = {
  sm: { inline: 26, stacked: 48, text: 'text-base' },
  md: { inline: 30, stacked: 64, text: 'text-lg' },
  lg: { inline: 40, stacked: 42, text: 'text-2xl' },
}

/**
 * Mark plus name, as one unit: the top of every sidebar and every auth page.
 *
 * Two-tone, as the pack sets it — "FlowZa" in the logo's navy (`--brand-ink`) and "Sign" in the
 * signature green (`--brand-signature`), in the script face and a third larger than the sans word
 * beside it, sitting a touch below its baseline, which is how the artwork sets the two. Both
 * tokens follow the theme: navy on a light page is a pale indigo on a dark one, and the green
 * lightens the same way. The green is `--color-signature-600` (#0f7a38) rather than the artwork's
 * brighter #15893f, and the difference is measured rather than aesthetic: the brighter one is
 * 4.49:1 on white, a hair under the 4.5:1 AA floor for the sidebar's 16 px word, and the darker
 * is 5.44:1.
 *
 * On an accent-painted panel both words go to `--text-on-accent`: green on a saturated indigo is
 * the one ground the brand green cannot clear, and a wordmark that is half-legible is worse than a
 * wordmark that is one colour.
 */
export function Wordmark({
  size = 'md',
  className = '',
  tone = 'inherit',
  layout = 'inline',
}: WordmarkProps) {
  const dims = WORDMARK_SIZE[size]
  const onAccent = tone === 'on-accent'
  const stacked = layout === 'stacked'
  const name = (
    <span
      className={`${dims.text} inline-flex items-baseline gap-[0.16em] font-bold leading-none tracking-tight`}
    >
      <span className={onAccent ? 'text-text-on-accent' : 'text-brand-ink'}>FlowZa</span>
      {/* A real space between the words. The flex gap sets them apart on screen and ignores this
          text node; a screen reader, a find-in-page and a copy would otherwise get "FlowZaSign". */}{' '}
      <span
        className={`font-script relative top-[0.08em] text-[1.35em] font-bold tracking-normal ${
          onAccent ? 'text-text-on-accent' : 'text-brand-signature'
        }`}
      >
        Sign
      </span>
    </span>
  )
  return stacked ? (
    <span className={`inline-flex flex-col items-center gap-3 ${className}`}>
      <Logo size={dims.stacked} inverted={onAccent} />
      {name}
    </span>
  ) : (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <Logo size={dims.inline} inverted={onAccent} />
      {name}
    </span>
  )
}
